  (function () {
    const CHECK_INTERVAL_MS = 300;
    const MAX_WAIT_MS = 10000;
    const APO_PRIMARY_CTA_SELECTOR = '.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned';
    const MOBILE_BREAKPOINT = window.matchMedia('(max-width: 999px)');
    const mwApoUtils = window.mwApoUtils || {};
    const environmentModule = window.metaboxConfiguratorEnvironments || {};

    let scrollLockTimer = null;
    let viewportSyncRafId = null;
    let configureMediaRepinTimer = null;
    const pageScrollLocks = new Set();
    let pageScrollLockTop = null;

    function setConfigurePendingState(productRoot, isPending) {
      if (!productRoot) return;
      productRoot.classList.toggle('product--configure-pending', Boolean(isPending));
    }

    function lockPageScroll(reason) {
      if (!reason) return;

      if (!pageScrollLocks.size) {
        pageScrollLockTop = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `${-pageScrollLockTop}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }

      pageScrollLocks.add(reason);
    }

    function unlockPageScroll(reason) {
      if (!reason || !pageScrollLocks.has(reason)) return;

      pageScrollLocks.delete(reason);
      if (pageScrollLocks.size) return;

      const scrollTop = Number(pageScrollLockTop) || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      pageScrollLockTop = null;

      window.scrollTo(0, scrollTop);
    }

    function syncViewportHeightVar() {
      const viewportHeight =
        window.visualViewport && Number(window.visualViewport.height) > 0
          ? window.visualViewport.height
          : window.innerHeight;
      if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

      document.documentElement.style.setProperty('--metabox-visual-viewport-height', `${Math.round(viewportHeight)}px`);
    }

    function scheduleViewportHeightSync() {
      if (viewportSyncRafId) return;
      viewportSyncRafId = window.requestAnimationFrame(() => {
        viewportSyncRafId = null;
        syncViewportHeightVar();
      });
    }

    function pinConfiguratorMediaIfNeeded() {
      const isConfigureModeActive = document.documentElement.classList.contains('mw-configure-mode-active');
      if (!isConfigureModeActive) return;

      const configuratorMedia = document.querySelector(
        '.product-gallery__carousel .product-gallery__media[data-media-id="configurator"], .product-gallery__carousel .product-gallery__media--configurator'
      );
      if (!configuratorMedia) return;

      configuratorMedia.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'center',
      });

      const navButtons = document.querySelectorAll(
        'product-gallery-navigation [data-media-id], carousel-navigation [data-media-id]'
      );
      navButtons.forEach((button) => {
        const isConfigurator = button.getAttribute('data-media-id') === 'configurator';
        button.setAttribute('aria-current', isConfigurator ? 'true' : 'false');
      });
    }

    function scheduleConfiguratorMediaRepin() {
      if (configureMediaRepinTimer) {
        window.clearTimeout(configureMediaRepinTimer);
      }

      configureMediaRepinTimer = window.setTimeout(() => {
        pinConfiguratorMediaIfNeeded();
        configureMediaRepinTimer = null;
      }, 180);
    }

    function findOrRecoverPrimaryApoButton(productInfo, apoBlock) {
      const localButton = apoBlock?.querySelector(APO_PRIMARY_CTA_SELECTOR);
      if (localButton) return localButton;

      const productForm = productInfo?.querySelector('form[id^="product-form-main-"]');
      if (!productForm?.id) return null;

      const misplacedButton = Array.from(document.querySelectorAll('.mw-apo-add-to-cart-cloned')).find(
        (button) => button.getAttribute('form') === productForm.id
      );
      if (!misplacedButton || !apoBlock) return null;

      misplacedButton.classList.add('mw-apo-add-to-cart-custom');
      misplacedButton.dataset.tactileMwApoRecovered = 'true';
      apoBlock.appendChild(misplacedButton);
      console.warn('[Tactile MW APO] Recovered primary CTA from outside the configurator');
      return misplacedButton;
    }

    function getConfigureContext() {
      const productInfo = document.querySelector('safe-sticky.product-info, .product-info');
      if (!productInfo) return null;
      const productRoot = productInfo.closest('.product');
      if (!productRoot) return null;
      productRoot.dataset.configureShell = 'true';

      const blockList = productInfo.querySelector('.product-info__block-list');
      if (!blockList) return null;

      const apoBlock = mwApoUtils.findApoBlock ? mwApoUtils.findApoBlock(document) : null;
      if (!apoBlock || !blockList.contains(apoBlock)) return null;

      const apoContainer = apoBlock.querySelector('.mw-apo-container');
      if (!apoContainer) return null;

      const goCustomBlock = Array.from(
        blockList.querySelectorAll('.product-info__block-item[data-block-type="button"]')
      ).find((item) => item.querySelector('.custom-contact-button'));

      const priceNode = apoBlock.querySelector('.mw-price-above-cart');
      const addToCartBtn = findOrRecoverPrimaryApoButton(productInfo, apoBlock);
      if (!priceNode || !addToCartBtn || !addToCartBtn.parentNode) return null;

      return {
        productRoot,
        productInfo,
        blockList,
        apoBlock,
        apoContainer,
        goCustomBlock,
        priceNode,
        addToCartBtn,
      };
    }

    function buildConfigureLayout(ctx) {
      const { blockList, apoBlock, goCustomBlock, priceNode, addToCartBtn } = ctx;

      const sliderTrack = blockList.querySelector('[data-configure-slider-track]');
      const contentPanel = sliderTrack?.querySelector('[data-configure-content-panel]');
      let actionsInlineAnchor = blockList.querySelector('[data-configure-actions-anchor]');
      const stickyFooter = apoBlock.querySelector('[data-configure-sticky-footer]');
      const actionsGrid = stickyFooter?.querySelector('[data-configure-actions-grid]');
      const toggleButton = actionsGrid?.querySelector('.product-info__configure-toggle');

      if (!sliderTrack || !contentPanel || !actionsInlineAnchor || !stickyFooter || !actionsGrid || !toggleButton) {
        return false;
      }

      addToCartBtn.classList.add('button', 'w-full', 'add-to-cart-custom');
      apoBlock.classList.add('product-info__slider-panel', 'product-info__slider-panel--apo');

      if (contentPanel.parentNode !== sliderTrack) {
        sliderTrack.prepend(contentPanel);
      }

      if (apoBlock.parentNode !== sliderTrack) {
        sliderTrack.appendChild(apoBlock);
      }

      if (actionsInlineAnchor.parentNode !== contentPanel) {
        contentPanel.appendChild(actionsInlineAnchor);
      }

      actionsInlineAnchor = contentPanel.querySelector('[data-configure-actions-anchor]');
      if (!actionsInlineAnchor) return false;

      if (priceNode.parentNode !== actionsGrid) {
        actionsGrid.prepend(priceNode);
      }

      if (addToCartBtn.parentNode !== actionsGrid) {
        actionsGrid.appendChild(addToCartBtn);
      }

      if (goCustomBlock && goCustomBlock.parentNode !== stickyFooter) {
        stickyFooter.appendChild(goCustomBlock);
      }

      ctx.actionsGrid = actionsGrid;
      ctx.stickyFooter = stickyFooter;
      ctx.toggleButton = toggleButton;
      ctx.actionsInlineAnchor = actionsInlineAnchor;
      return true;
    }

    function setupOptionsViewSwitcher(ctx) {
      const { apoContainer } = ctx;
      if (!apoContainer || !apoContainer.parentNode) return false;

      const optionsFrame = ctx.apoBlock.querySelector('[data-configure-options-frame]');
      const optionsTabs = optionsFrame?.querySelector('[data-configure-options-tabs]');
      const productOptionsButton = optionsTabs?.querySelector('[data-configure-options-tab="product"]');
      const environmentOptionsButton = optionsTabs?.querySelector('[data-configure-options-tab="environment"]');
      const fullscreenToggleButton = ctx.productRoot?.querySelector('[data-configure-fullscreen-toggle]');
      const optionsTrack = optionsFrame?.querySelector('[data-configure-options-track]');
      const productOptionsPanel = optionsTrack?.querySelector('[data-configure-options-panel="product"]');
      const environmentOptionsPanel = optionsTrack?.querySelector('[data-configure-options-panel="environment"]');
      if (
        !optionsFrame ||
        !optionsTabs ||
        !productOptionsButton ||
        !environmentOptionsButton ||
        !optionsTrack ||
        !productOptionsPanel ||
        !environmentOptionsPanel
      ) {
        return false;
      }

      if (apoContainer.parentNode !== productOptionsPanel) {
        productOptionsPanel.appendChild(apoContainer);
      }

      let configureMenuContent = productOptionsPanel.querySelector('[data-configure-menu-content]');
      const configureMenuTemplate = productOptionsPanel.querySelector('[data-configure-menu-content-template]');

      if (!configureMenuContent && configureMenuTemplate?.content) {
        configureMenuContent = configureMenuTemplate.content.firstElementChild?.cloneNode(true) || null;
      }

      if (configureMenuContent && configureMenuContent.parentNode !== apoContainer) {
        apoContainer.appendChild(configureMenuContent);
      }

      ctx.optionsFrame = optionsFrame;
      ctx.productOptionsButton = productOptionsButton;
      ctx.environmentOptionsButton = environmentOptionsButton;
      ctx.fullscreenToggleButton = fullscreenToggleButton;
      ctx.productOptionsPanel = productOptionsPanel;
      ctx.environmentOptionsPanel = environmentOptionsPanel;
      return true;
    }

    function setOptionsView(ctx, viewName) {
      const { optionsFrame, productOptionsButton, environmentOptionsButton } = ctx;
      if (!optionsFrame) return;

      const nextView = viewName === 'environment' ? 'environment' : 'product';
      const isEnvironmentView = nextView === 'environment';

      optionsFrame.dataset.configureOptionsView = nextView;

      if (productOptionsButton) {
        const isActive = !isEnvironmentView;
        productOptionsButton.classList.toggle('is-active', isActive);
        productOptionsButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }

      if (environmentOptionsButton) {
        const isActive = isEnvironmentView;
        environmentOptionsButton.classList.toggle('is-active', isActive);
        environmentOptionsButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }

    }

    function resetMetaboxCameraCache() {
      if (!window.metaboxConfigurator) return;

      window.metaboxConfigurator.lastCameraPayload = null;
      window.metaboxConfigurator.resetCameraTargetPayload = null;

      if (typeof window.metaboxConfigurator.warmupResetCameraTarget === 'function') {
        window.requestAnimationFrame(() => {
          window.metaboxConfigurator.warmupResetCameraTarget();
        });
      }
    }

    function setFullscreenMode(ctx, enabled) {
      const { productRoot, fullscreenToggleButton } = ctx;
      if (!productRoot) return;

      const nextState = Boolean(enabled);
      ctx.isFullscreenMode = nextState;

      productRoot.classList.toggle('product--configure-fullscreen', nextState);
      document.documentElement.classList.toggle('mw-configure-fullscreen-active', nextState);
      resetMetaboxCameraCache();

      if (fullscreenToggleButton) {
        fullscreenToggleButton.setAttribute('aria-pressed', nextState ? 'true' : 'false');
        fullscreenToggleButton.setAttribute('aria-label', nextState ? 'Exit fullscreen' : 'Enter fullscreen');
        fullscreenToggleButton.setAttribute('title', nextState ? 'Exit fullscreen' : 'Enter fullscreen');
      }

      if (nextState) {
        lockPageScroll('configure-fullscreen');
        requestAnimationFrame(() => {
          scheduleViewportHeightSync();
          pinConfiguratorMediaIfNeeded();
        });
      } else {
        unlockPageScroll('configure-fullscreen');
        scheduleViewportHeightSync();
      }
    }

    function cloneGoCustomTrigger(goCustomBlock) {
      const goCustomButton = goCustomBlock?.querySelector('.custom-contact-button');
      if (!goCustomButton) return null;

      // Keep the mirrored CTA styling, but never clone the modal/form. HubSpot
      // listeners are not copied by cloneNode(), so a cloned form looks bound
      // (the data attribute is copied) while its submission is not captured.
      const goCustomClone = goCustomBlock.cloneNode(false);
      goCustomClone.appendChild(goCustomButton.cloneNode(true));
      goCustomClone.setAttribute('data-configure-go-custom-clone', 'true');
      return goCustomClone;
    }

    function setupInfoFooterClone(ctx) {
      const { blockList, actionsGrid, goCustomBlock, actionsInlineAnchor, priceNode, addToCartBtn } = ctx;
      let infoFooter = blockList.querySelector('[data-configure-info-footer]');
      if (!infoFooter) {
        infoFooter = document.createElement('div');
        infoFooter.className = 'product-info__sticky-actions product-info__sticky-actions--mirror';
        infoFooter.setAttribute('data-configure-info-footer', 'true');

        const infoActionsGrid = actionsGrid.cloneNode(true);
        infoActionsGrid.setAttribute('data-configure-actions-grid-clone', 'true');
        infoFooter.appendChild(infoActionsGrid);

        if (goCustomBlock) {
          const goCustomClone = cloneGoCustomTrigger(goCustomBlock);
          if (goCustomClone) infoFooter.appendChild(goCustomClone);
        }

        actionsInlineAnchor.appendChild(infoFooter);
      } else if (infoFooter.parentNode !== actionsInlineAnchor) {
        actionsInlineAnchor.appendChild(infoFooter);
      }

      const infoActionsGrid = infoFooter.querySelector('[data-configure-actions-grid-clone]');
      if (!infoActionsGrid) return false;

      const infoToggleButton = infoActionsGrid?.querySelector('.product-info__configure-toggle');
      let infoPriceNode = infoActionsGrid?.querySelector('.mw-price-above-cart');
      let infoAddToCartBtn = infoActionsGrid?.querySelector(
        '.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .mw-apo-add-to-cart-proxy'
      );

      if (!infoPriceNode) {
        infoPriceNode = document.createElement('div');
        infoPriceNode.className = 'mw-price-above-cart mb-2';
        infoPriceNode.setAttribute('data-mirror-price-observer', 'true');
        infoActionsGrid.prepend(infoPriceNode);
      }

      if (!infoAddToCartBtn) {
        const proxyButton = addToCartBtn.cloneNode(true);
        proxyButton.classList.remove('mw-apo-add-to-cart-custom');
        proxyButton.classList.add('mw-apo-add-to-cart-proxy');
        proxyButton.dataset.configureProxyPrepared = 'true';
        proxyButton.removeAttribute('id');
        infoActionsGrid.appendChild(proxyButton);
        infoAddToCartBtn = proxyButton;
      }

      if (infoAddToCartBtn && !infoAddToCartBtn.dataset.configureProxyPrepared) {
        infoAddToCartBtn.classList.remove('mw-apo-add-to-cart-custom');
        infoAddToCartBtn.classList.add('mw-apo-add-to-cart-proxy');
        infoAddToCartBtn.dataset.configureProxyPrepared = 'true';
      }

      if (goCustomBlock && !infoFooter.querySelector('[data-configure-go-custom-clone]')) {
        const goCustomClone = cloneGoCustomTrigger(goCustomBlock);
        if (goCustomClone) infoFooter.appendChild(goCustomClone);
      }

      infoAddToCartBtn = infoActionsGrid?.querySelector('.mw-apo-add-to-cart-proxy');
      if (infoAddToCartBtn) {
        infoAddToCartBtn.classList.add('button', 'w-full', 'add-to-cart-custom');
      }

      function syncMirrorPrice() {
        if (!infoPriceNode || !priceNode) return;
        infoPriceNode.innerHTML = priceNode.innerHTML;
      }

      syncMirrorPrice();

      if (priceNode && !priceNode.dataset.mirrorPriceObserverBound) {
        const priceObserver = new MutationObserver(syncMirrorPrice);
        priceObserver.observe(priceNode, { childList: true, subtree: true, characterData: true });
        priceNode.dataset.mirrorPriceObserverBound = 'true';
      }

      ctx.infoToggleButton = infoToggleButton;
      ctx.infoAddToCartBtn = infoAddToCartBtn;
      return true;
    }

    function scrollToConfiguratorMedia() {
      const configuratorMedia = document.querySelector(
        '.product-gallery__media[data-media-id="configurator"], .product-gallery__media--configurator'
      );
      if (!configuratorMedia) return;

      const gallery = configuratorMedia.closest('.product-gallery');

      configuratorMedia.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      if (MOBILE_BREAKPOINT.matches && gallery) {
        gallery.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }

    function enterConfigureModeIfConfiguratorVisible(ctx, mediaCell) {
      if (MOBILE_BREAKPOINT.matches) return;
      if (document.documentElement.classList.contains('mw-configure-mode-active')) return;
      if (mediaCell?.getAttribute?.('data-media-id') !== 'configurator') return;

      setConfigureMode(ctx, true);
    }

    function selectNonConfiguratorMedia(ctx) {
      const carousel = ctx.productRoot?.querySelector('.product-gallery__carousel');
      if (!carousel || carousel.selectedCell?.getAttribute?.('data-media-id') !== 'configurator') return;

      const cells = Array.from(carousel.cells || []);
      const selectedIndex = typeof carousel.selectedIndex === 'number' ? carousel.selectedIndex : cells.indexOf(carousel.selectedCell);
      let fallbackIndex = -1;

      for (let index = selectedIndex - 1; index >= 0; index -= 1) {
        if (cells[index]?.getAttribute('data-media-id') !== 'configurator') {
          fallbackIndex = index;
          break;
        }
      }

      if (fallbackIndex < 0) {
        fallbackIndex = cells.findIndex((cell, index) => {
          return index > selectedIndex && cell.getAttribute('data-media-id') !== 'configurator';
        });
      }

      if (fallbackIndex < 0) return;

      carousel.select?.(fallbackIndex, { instant: true });
    }

    function syncMobileConfiguratorGalleryState(ctx, isConfigureMode) {
      const shouldHide = MOBILE_BREAKPOINT.matches && !isConfigureMode;
      const configuratorControls = ctx.productRoot?.querySelectorAll(
        '.product-gallery__media[data-media-id="configurator"], .product-gallery__thumbnail[data-media-id="configurator"], carousel-navigation [data-media-id="configurator"]'
      );

      if (shouldHide) {
        selectNonConfiguratorMedia(ctx);
      }

      configuratorControls?.forEach((element) => {
        element.toggleAttribute('hidden', shouldHide);
      });
    }

    function setConfigureMode(ctx, enabled) {
      const { productRoot, productInfo, toggleButton, infoToggleButton, blockList } = ctx;
      if (!enabled && ctx.isFullscreenMode) {
        setFullscreenMode(ctx, false);
      }

      if (productRoot) {
        productRoot.classList.toggle('product--configure-mode', enabled);
      }
      productInfo.classList.toggle('product-info--configure-mode', enabled);
      document.documentElement.classList.toggle('mw-configure-mode-active', enabled);
      syncMobileConfiguratorGalleryState(ctx, enabled);
      scheduleViewportHeightSync();

      if (enabled) {
        toggleButton.textContent = 'Go back';
        if (infoToggleButton) {
          infoToggleButton.textContent = 'Go back';
        }
        blockList.classList.add('mw-apo-slider-active');
        requestAnimationFrame(scrollToConfiguratorMedia);
        window.dispatchEvent(new CustomEvent('metabox:overlay-show-requested'));
        if (MOBILE_BREAKPOINT.matches) {
          setFullscreenMode(ctx, true);
        }
      } else {
        toggleButton.textContent = 'Configure';
        if (infoToggleButton) {
          infoToggleButton.textContent = 'Configure';
        }
        blockList.classList.remove('mw-apo-slider-active');
        setOptionsView(ctx, 'product');
      }
    }

    function applyEditModeState(ctx) {
      const editSession = window.mwApoEditSession;
      if (!editSession?.isEditing) return;

      if (ctx.addToCartBtn) {
        ctx.addToCartBtn.textContent = 'Update';
        ctx.addToCartBtn.dataset.mwApoEditLabelApplied = 'true';
      }

      if (ctx.infoAddToCartBtn) {
        ctx.infoAddToCartBtn.textContent = 'Update';
        ctx.infoAddToCartBtn.dataset.mwApoEditLabelApplied = 'true';
      }

      setConfigureMode(ctx, true);
    }

    function lockPageScrollFor(durationMs) {
      if (scrollLockTimer) {
        clearTimeout(scrollLockTimer);
        scrollLockTimer = null;
      }

      lockPageScroll('apo-error');

      scrollLockTimer = window.setTimeout(() => {
        unlockPageScroll('apo-error');
        scrollLockTimer = null;
      }, durationMs);
    }

    function scrollToFirstApoError(ctx) {
      const { apoBlock, apoContainer } = ctx;
      const targetElement = mwApoUtils.getFirstApoErrorElement
        ? mwApoUtils.getFirstApoErrorElement(apoContainer)
        : null;
      if (!targetElement) return;

      setOptionsView(ctx, 'product');

      const apoPanel = apoBlock.closest('.product-info__slider-panel--apo');
      const scrollContainer = ctx.apoContainer || apoPanel;
      if (mwApoUtils.scrollContainerToElement) {
        mwApoUtils.scrollContainerToElement(scrollContainer, targetElement, 16);
      }
    }

    function bindConfigureEvents(ctx) {
      const {
        productRoot,
        productInfo,
        toggleButton,
        infoToggleButton,
        addToCartBtn,
        infoAddToCartBtn,
        productOptionsButton,
        environmentOptionsButton,
        fullscreenToggleButton,
      } = ctx;

      if (productOptionsButton && productOptionsButton.dataset.configureTabBound !== 'true') {
        productOptionsButton.addEventListener('click', () => {
          setOptionsView(ctx, 'product');
        });
        productOptionsButton.dataset.configureTabBound = 'true';
      }

      if (environmentOptionsButton && environmentOptionsButton.dataset.configureTabBound !== 'true') {
        environmentOptionsButton.addEventListener('click', () => {
          setOptionsView(ctx, 'environment');
        });
        environmentOptionsButton.dataset.configureTabBound = 'true';
      }

      if (fullscreenToggleButton && fullscreenToggleButton.dataset.configureFullscreenBound !== 'true') {
        fullscreenToggleButton.addEventListener('click', () => {
          const isConfigureMode = toggleButton.textContent.trim().toLowerCase() === 'go back';
          if (!isConfigureMode) {
            setConfigureMode(ctx, true);
          }

          setFullscreenMode(ctx, !ctx.isFullscreenMode);
        });
        fullscreenToggleButton.dataset.configureFullscreenBound = 'true';
      }

      if (toggleButton.dataset.configureBound !== 'true') {
        toggleButton.addEventListener('click', () => {
          const isConfigureMode = toggleButton.textContent.trim().toLowerCase() === 'go back';
          setConfigureMode(ctx, !isConfigureMode);
        });
        toggleButton.dataset.configureBound = 'true';
      }

      if (infoToggleButton && infoToggleButton.dataset.configureBound !== 'true') {
        infoToggleButton.textContent = 'Configure';
        infoToggleButton.addEventListener('click', () => {
          const isConfigureMode = infoToggleButton.textContent.trim().toLowerCase() === 'go back';
          setConfigureMode(ctx, !isConfigureMode);
        });
        infoToggleButton.dataset.configureBound = 'true';
      }

      const carousel = productRoot?.querySelector('.product-gallery__carousel');
      if (carousel && carousel.dataset.configureSelectionBound !== 'true') {
        const handleConfiguratorSelection = (event) => {
          const selectedCell = event.detail?.cell || carousel.selectedCell;
          enterConfigureModeIfConfiguratorVisible(ctx, selectedCell);
        };

        carousel.addEventListener('carousel:change', handleConfiguratorSelection);
        carousel.addEventListener('carousel:settle', handleConfiguratorSelection);
        requestAnimationFrame(() => {
          enterConfigureModeIfConfiguratorVisible(ctx, carousel.selectedCell);
        });
        carousel.dataset.configureSelectionBound = 'true';
      }

      if (carousel && carousel.dataset.configureMobileVisibilityBound !== 'true') {
        const syncMobileConfiguratorVisibility = () => {
          syncMobileConfiguratorGalleryState(ctx, document.documentElement.classList.contains('mw-configure-mode-active'));
        };

        window.addEventListener('resize', syncMobileConfiguratorVisibility, { passive: true });
        window.addEventListener('orientationchange', syncMobileConfiguratorVisibility, { passive: true });
        carousel.addEventListener('carousel:filter', () => {
          requestAnimationFrame(syncMobileConfiguratorVisibility);
        });
        requestAnimationFrame(syncMobileConfiguratorVisibility);
        carousel.dataset.configureMobileVisibilityBound = 'true';
      }

      if (productInfo.dataset.configureAddToCartGuardBound !== 'true') {
        productInfo.addEventListener(
          'click',
          (event) => {
            const clickedButton = event.target.closest(
              '.mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, .mw-apo-add-to-cart-proxy'
            );
            if (!clickedButton) return;

            const isManagedButton = clickedButton === addToCartBtn || (infoAddToCartBtn && clickedButton === infoAddToCartBtn);
            if (!isManagedButton) return;
            if (!mwApoUtils.hasMissingRequiredOptions || !mwApoUtils.hasMissingRequiredOptions(ctx.apoContainer)) return;

            lockPageScrollFor(2000);
            setConfigureMode(ctx, true);

            requestAnimationFrame(() => {
              scrollToFirstApoError(ctx);
            });
          },
          true
        );

        productInfo.dataset.configureAddToCartGuardBound = 'true';
      }

      if (infoAddToCartBtn && infoAddToCartBtn.dataset.proxyBound !== 'true') {
        infoAddToCartBtn.addEventListener(
          'click',
          () => {
            addToCartBtn.click();
          },
          true
        );
        infoAddToCartBtn.dataset.proxyBound = 'true';
      }

      if (addToCartBtn.dataset.apoScrollOverrideBound !== 'true') {
        addToCartBtn.addEventListener(
          'click',
          () => {
            window.setTimeout(() => {
              scrollToFirstApoError(ctx);
            }, 60);
          },
          true
        );

        addToCartBtn.dataset.apoScrollOverrideBound = 'true';
      }

      if (productInfo.dataset.configureEscapeBound !== 'true') {
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          if (MOBILE_BREAKPOINT.matches) return;
          if (!ctx.isFullscreenMode) return;

          event.preventDefault();
          setFullscreenMode(ctx, false);
        });

        productInfo.dataset.configureEscapeBound = 'true';
      }

    }

    function initConfigureToggle() {
      const ctx = getConfigureContext();
      if (!ctx) return false;

      if (!buildConfigureLayout(ctx)) return false;
      if (!setupOptionsViewSwitcher(ctx)) return false;
      setOptionsView(ctx, 'product');
      if (environmentModule.setup) {
        environmentModule.setup(ctx);
      }
      if (!setupInfoFooterClone(ctx)) return false;
      bindConfigureEvents(ctx);
      setFullscreenMode(ctx, false);
      setConfigureMode(ctx, false);
      setConfigurePendingState(ctx.productRoot, false);
      applyEditModeState(ctx);
      return true;
    }

    window.addEventListener('resize', scheduleViewportHeightSync, { passive: true });
    window.addEventListener('orientationchange', scheduleViewportHeightSync, { passive: true });
    window.addEventListener('orientationchange', scheduleConfiguratorMediaRepin, { passive: true });
    window.addEventListener('pageshow', scheduleViewportHeightSync, { passive: true });
    window.addEventListener('scroll', scheduleViewportHeightSync, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleViewportHeightSync, { passive: true });
      window.visualViewport.addEventListener('scroll', scheduleViewportHeightSync, { passive: true });
    }
    syncViewportHeightVar();

    const checkInterval = window.setInterval(() => {
      if (initConfigureToggle()) {
        window.clearInterval(checkInterval);
        window.clearTimeout(timeoutId);
      }
    }, CHECK_INTERVAL_MS);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(checkInterval);

      const pendingProduct = document.querySelector('.product.product--has-configurator.product--configure-pending');
      const hasLoadedApoOptions = Boolean(pendingProduct?.querySelector('.mw-apo-container .mw-option'));
      if (!pendingProduct || !hasLoadedApoOptions) return;

      // Never leave a fully loaded configurator permanently invisible because a
      // third-party class or insertion point changed. This is a degraded UI, but
      // customers can still see the options while the recovery event is monitored.
      setConfigurePendingState(pendingProduct, false);
      pendingProduct.dataset.configureFallback = 'true';
      console.error('[Tactile MW APO] Configurator initialization timed out; displaying fallback UI');
      window.dispatchEvent(
        new CustomEvent('tactile:mw-apo-recovery-failed', {
          detail: { reason: 'configure-timeout' },
        })
      );
    }, MAX_WAIT_MS);
  })();
